import React, { useCallback, useEffect, useState } from 'react';
import { Card, Btn, Spinner, Alert, EmptyState, Modal, FormField, Input, Select } from '../../components/UI';
import { api } from '../../utils/api';

// Community → Forum: categories (Training content, Customers, …), posts and replies.
// Ported from HB Compass Forum. Anyone can propose a category; an administrator
// (SUPERADMIN / ADM) approves, renames, reorders and deletes categories.

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const textareaStyle = { width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', fontSize: '0.88rem', fontFamily: 'var(--font-body)', resize: 'vertical' };
const pill = (on) => ({
  padding: '7px 16px', borderRadius: 999, border: '1px solid var(--line)', cursor: 'pointer',
  background: on ? 'var(--ink)' : 'var(--canvas-white)', color: on ? '#fff' : 'var(--ink)',
  fontSize: '0.82rem', fontWeight: 500, fontFamily: 'var(--font-body)',
});
const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.78rem', color: 'var(--ink-soft)', textDecoration: 'underline', textUnderlineOffset: 2 };

function ManageCategoriesModal({ open, onClose, categories, onChanged }) {
  const [list, setList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setList(categories.filter(c => c.status !== 'pending')); setError(null); setEditingId(null); } }, [open, categories]);
  const pending = categories.filter(c => c.status === 'pending');
  const run = async (fn) => { setError(null); try { await fn(); onChanged(); } catch (e) { setError(e.message); } };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    setList(next);
    run(() => api.forum.reorderCategories(next.map(c => c.id)));
  };

  return (
    <Modal open={open} onClose={onClose} title="Manage categories">
      {error && <div style={{ marginBottom: 12 }}><Alert type="error">{error}</Alert></div>}
      {pending.length > 0 && (
        <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--ink-soft)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Waiting for approval ({pending.length})</div>
          {pending.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px' }}>
              <div style={{ flex: 1, fontSize: '0.88rem' }}>{c.name}{c.proposedBy && <span style={{ color: 'var(--ink-faint)', fontSize: '0.76rem' }}> · proposed by {c.proposedBy}</span>}</div>
              <Btn size="sm" onClick={() => run(() => api.forum.approveCategory(c.id))}>Approve</Btn>
              <Btn size="sm" variant="ghost" onClick={() => { if (window.confirm(`Reject “${c.name}”?`)) run(() => api.forum.rejectCategory(c.id)); }}>Reject</Btn>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
        {list.map((c, i) => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button onClick={() => move(i, -1)} disabled={i === 0} style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1, lineHeight: 1, padding: 1 }}>▲</button>
              <button onClick={() => move(i, 1)} disabled={i === list.length - 1} style={{ background: 'none', border: 'none', cursor: i === list.length - 1 ? 'default' : 'pointer', opacity: i === list.length - 1 ? 0.3 : 1, lineHeight: 1, padding: 1 }}>▼</button>
            </div>
            {editingId === c.id ? (
              <>
                <input autoFocus value={editingName} onChange={e => setEditingName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && editingName.trim()) run(async () => { await api.forum.renameCategory(c.id, editingName.trim()); setEditingId(null); }); }}
                  style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', fontSize: '0.86rem' }} />
                <Btn size="sm" disabled={!editingName.trim()} onClick={() => run(async () => { await api.forum.renameCategory(c.id, editingName.trim()); setEditingId(null); })}>Save</Btn>
                <Btn size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Btn>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: '0.88rem' }}>{c.name}</span>
                <button style={linkBtn} onClick={() => { setEditingId(c.id); setEditingName(c.name); }}>Rename</button>
                <button style={{ ...linkBtn, color: 'var(--danger)' }} onClick={() => { if (window.confirm(`Delete category “${c.name}”? Only empty categories can be deleted.`)) run(() => api.forum.deleteCategory(c.id)); }}>Delete</button>
              </>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

function ProposeCategoryModal({ open, onClose, isAdmin, onCreated }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setName(''); setError(null); } }, [open]);
  async function submit() {
    if (!name.trim()) return;
    setSaving(true); setError(null);
    try { await api.forum.proposeCategory(name.trim()); onCreated(); } catch (e) { setError(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal open={open} onClose={onClose} title={isAdmin ? 'New category' : 'Propose a category'}>
      {error && <div style={{ marginBottom: 12 }}><Alert type="error">{error}</Alert></div>}
      {!isAdmin && <div style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', marginBottom: 14 }}>An administrator reviews new categories before they appear for everyone.</div>}
      <FormField label="Category name" required>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Hikes & Expeditions" maxLength={100} />
      </FormField>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} loading={saving} disabled={!name.trim()}>{isAdmin ? 'Create' : 'Send proposal'}</Btn>
      </div>
    </Modal>
  );
}

function NewPostModal({ open, onClose, categories, defaultCategoryId, onCreated }) {
  const [categoryId, setCategoryId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (open) { setCategoryId(defaultCategoryId || (categories[0]?.id ?? '')); setTitle(''); setBody(''); setError(null); }
  }, [open, defaultCategoryId, categories]);
  async function submit() {
    if (!categoryId || !title.trim() || !body.trim()) return;
    setSaving(true); setError(null);
    try { const r = await api.forum.createPost({ categoryId: Number(categoryId), title: title.trim(), body: body.trim() }); onCreated(r.id, Number(categoryId)); }
    catch (e) { setError(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal open={open} onClose={onClose} title="New post">
      {error && <div style={{ marginBottom: 12 }}><Alert type="error">{error}</Alert></div>}
      <FormField label="Category" required>
        <Select value={categoryId} onChange={e => setCategoryId(e.target.value)}>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
      </FormField>
      <FormField label="Title" required>
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="What is it about?" maxLength={300} />
      </FormField>
      <FormField label="Post" required>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={6} style={textareaStyle} />
      </FormField>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} loading={saving} disabled={!categoryId || !title.trim() || !body.trim()}>Post</Btn>
      </div>
    </Modal>
  );
}

// `indented` = one-level threaded reply; only top-level replies can be replied to.
function ReplyCard({ r, indented, onDelete, onReply }) {
  return (
    <div style={{ padding: '12px 14px', background: 'var(--canvas-warm)', borderRadius: 'var(--radius-md)', marginLeft: indented ? 28 : 0, borderLeft: indented ? '2px solid var(--ink-faint)' : 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{r.author}</span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '0.74rem', color: 'var(--ink-soft)' }}>{formatWhen(r.createdAt)}</span>
          {r.canDelete && <button onClick={() => onDelete(r.id)} style={{ ...linkBtn, color: 'var(--danger)', textDecoration: 'none' }}>Delete</button>}
        </div>
      </div>
      <p style={{ margin: '6px 0 0', fontSize: '0.88rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{r.body}</p>
      {!indented && <button onClick={() => onReply({ id: r.id, name: r.author })} style={{ ...linkBtn, marginTop: 4 }}>Reply</button>}
    </div>
  );
}

function PostDetail({ postId, onBack, onDeleted }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const load = useCallback(() => { api.forum.post(postId).then(setData).catch(e => setError(e.message)); }, [postId]);
  useEffect(() => { setData(null); setReplyingTo(null); load(); }, [postId, load]);

  async function sendReply() {
    const body = reply.trim();
    if (!body) return;
    setSending(true); setError(null);
    try { await api.forum.reply(postId, body, replyingTo?.id ?? null); setReply(''); setReplyingTo(null); load(); }
    catch (e) { setError(e.message); } finally { setSending(false); }
  }
  async function deletePost() {
    if (!window.confirm('Delete this post and all its replies?')) return;
    try { await api.forum.deletePost(postId); onDeleted(); } catch (e) { setError(e.message); }
  }
  async function deleteReply(id) {
    if (!window.confirm('Delete this reply?')) return;
    try { await api.forum.deleteReply(id); load(); } catch (e) { setError(e.message); }
  }

  if (!data) return error ? <Alert type="error">{error}</Alert> : <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>;
  const { post, replies } = data;
  const top = replies.filter(r => r.parentId == null);
  const childrenOf = (id) => replies.filter(r => r.parentId === id);

  return (
    <div>
      <button onClick={onBack} style={{ ...linkBtn, fontSize: '0.85rem' }}>← Back to {post.categoryName}</button>
      {error && <div style={{ marginTop: 12 }}><Alert type="error">{error}</Alert></div>}
      <div style={{ marginTop: 16, paddingBottom: 16, borderBottom: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', margin: 0 }}>{post.title}</h2>
          {post.canDelete && <Btn variant="ghost" size="sm" onClick={deletePost}>Delete</Btn>}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginTop: 4 }}>{post.author} · {formatWhen(post.createdAt)}</div>
        <p style={{ marginTop: 14, fontSize: '0.92rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{post.body}</p>
      </div>
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ink-soft)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{replies.length} {replies.length === 1 ? 'reply' : 'replies'}</div>
        {top.map(r => (
          <div key={r.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ReplyCard r={r} onDelete={deleteReply} onReply={setReplyingTo} />
            {childrenOf(r.id).map(ch => <ReplyCard key={ch.id} r={ch} indented onDelete={deleteReply} onReply={setReplyingTo} />)}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 20 }}>
        {replyingTo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: '0.8rem', color: 'var(--ink-soft)' }}>
            Replying to <strong>{replyingTo.name}</strong> <button style={linkBtn} onClick={() => setReplyingTo(null)}>Cancel</button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={2}
            placeholder={replyingTo ? `Reply to ${replyingTo.name}…` : 'Write a reply…'}
            style={{ ...textareaStyle, flex: 1, resize: 'none' }} />
          <Btn onClick={sendReply} loading={sending} disabled={!reply.trim()}>Reply</Btn>
        </div>
      </div>
    </div>
  );
}

export default function ForumPage() {
  const [categories, setCategories] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState(null); // null = all
  const [posts, setPosts] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [showPropose, setShowPropose] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const approved = categories.filter(c => c.status !== 'pending');
  const pendingCount = categories.length - approved.length;

  const loadCategories = useCallback(() => {
    api.forum.categories().then(d => {
      setCategories(d.categories || []);
      setIsAdmin(!!d.canManage);
      setActiveCategoryId(prev => (prev != null && !(d.categories || []).some(c => c.id === prev) ? null : prev));
    }).catch(e => setError(e.message));
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const loadPosts = useCallback(() => { setPosts(null); api.forum.posts(activeCategoryId).then(setPosts).catch(e => setError(e.message)); }, [activeCategoryId]);
  useEffect(() => { loadPosts(); }, [loadPosts]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.86rem', color: 'var(--ink-soft)' }}>Share training content, customer stories and questions with the whole community.</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="outline" onClick={() => setShowPropose(true)}>{isAdmin ? '+ Category' : 'Propose a category'}</Btn>
          <Btn onClick={() => setShowNew(true)} disabled={!approved.length}>+ New post</Btn>
        </div>
      </div>
      {error && <div style={{ marginTop: 14 }}><Alert type="error">{error}</Alert></div>}
      {notice && <div style={{ marginTop: 14 }}><Alert type="info">{notice}</Alert></div>}

      {!selectedPostId && (
        <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
          <button onClick={() => setActiveCategoryId(null)} style={pill(activeCategoryId === null)}>All</button>
          {approved.map(c => <button key={c.id} onClick={() => setActiveCategoryId(c.id)} style={pill(activeCategoryId === c.id)}>{c.name}</button>)}
          {isAdmin && (
            <button onClick={() => setShowManage(true)} style={{ ...pill(false), borderStyle: 'dashed', color: 'var(--ink-soft)', background: 'transparent' }}>
              Manage categories{pendingCount ? ` (${pendingCount} waiting)` : ''}
            </button>
          )}
        </div>
      )}

      <Card style={{ marginTop: 16, padding: '18px 22px' }}>
        {selectedPostId ? (
          <PostDetail postId={selectedPostId} onBack={() => setSelectedPostId(null)} onDeleted={() => { setSelectedPostId(null); loadPosts(); }} />
        ) : posts === null ? <div style={{ padding: 30, textAlign: 'center' }}><Spinner /></div>
          : posts.length === 0 ? <EmptyState icon="🗨️" title="No posts yet" message="Be the first to post in this category." />
            : posts.map((p, i) => (
              <div key={p.id} onClick={() => setSelectedPostId(p.id)} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16,
                padding: '14px 4px', cursor: 'pointer', borderTop: i === 0 ? 'none' : '1px solid var(--line)',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-soft)', background: 'var(--canvas-warm)', padding: '2px 8px', borderRadius: 999 }}>{p.categoryName}</span>
                    <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>{p.title}</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginTop: 4 }}>{p.author} · {formatWhen(p.createdAt)}</div>
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', flexShrink: 0, whiteSpace: 'nowrap' }}>{p.replyCount} {p.replyCount === 1 ? 'reply' : 'replies'}</div>
              </div>
            ))}
      </Card>

      <NewPostModal open={showNew} onClose={() => setShowNew(false)} categories={approved} defaultCategoryId={activeCategoryId}
        onCreated={(id, cid) => { setShowNew(false); setActiveCategoryId(cid); setSelectedPostId(id); }} />
      <ManageCategoriesModal open={showManage} onClose={() => setShowManage(false)} categories={categories} onChanged={loadCategories} />
      <ProposeCategoryModal open={showPropose} onClose={() => setShowPropose(false)} isAdmin={isAdmin}
        onCreated={() => { setShowPropose(false); loadCategories(); if (!isAdmin) setNotice('Thanks — your category proposal was sent to an administrator.'); }} />
    </>
  );
}
